import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  CartItem,
  LegacyOrderRequest,
  ProcessOrderRequest,
  ShippingLocation,
  ShippingMethod,
  TransformedCartItem,
} from '../../types/api-types';

type ShipSet = {
  /** Format - YYYY-MM-DD */
  requestedDeliveryDate?: string | null;
  shippingMethodResults?: (ShippingMethod & {
    deliveryCalendar: {
      startDate?: string;
      availableDates?: string[];
    };
  })[];
};

type OrderDisplayDetails = Pick<
  LegacyOrderRequest,
  'accountNumber' | 'poNumber' | 'orderTotal' | 'orderDate' | 'deliveryPhone'
> & {
  items: (LegacyOrderRequest['items'][number] & {
    imagePath: string;
    originalItem?: string;
    isVolumeDiscountEligible?: boolean;
  })[];
};

type FinalOrderData = {
  groups: (
    | {
        type: 'APPLIANCES';
        displayDetails: OrderDisplayDetails;
        orderPayload: LegacyOrderRequest;
        shipSet: string;
        requestedDeliveryDate: string | null;
        shippingMethod: ShippingMethod['shippingMethod'];
        shippingLocation: ShippingLocation;
        totalTaxAmount?: string;
        orderSubTotal: string;
        orderTotal: string;
        orderReference?: string;
      }
    | {
        type: 'PARTS';
        displayDetails: OrderDisplayDetails;
        orderPayload: ProcessOrderRequest;
        shipSet: string;
        // requestedDeliveryDate: string | null;
        shippingMethod: string;
        // shippingLocation: ShippingLocation;
        requestedShipDate: string | null;
        totalTaxAmount?: string;
        orderSubTotal: string;
        deliveryHandlingCharges: string;
        orderTotal: string;
        orderReference?: string;
      }
  )[];
  originalCartData: {
    accountNumber: string;
    items: Pick<CartItem, 'sk' | 'item'>[];
    source?: 'cart' | 'quick-order';
  };
} | null;

export type CartSnapshot = {
  items: (TransformedCartItem & {
    shipSet: string | null;
    debouncedShipSet?: string | null;
    uncrateIndicator?: 'Y' | 'N';
    /** MM/DD/YYYY */
    requestedDeliveryDate?: string;
    isOrderable?: boolean;
    isVolumeDiscountEligible?: boolean; // For parts
    originalItem?: string;
    relationshipType?: 'Superseded';
    pim: {
      weight: number;
      points: number;
      geego_fedexable: 'Y' | 'N' | null;
      geego_top_load: string;
      geego_no_top_load: string;
      geego_distribution_start_date: string;
      geego_final_production_date: string;
      geego_uncrateind: string;
      geego_brand: string;
      item_catalog_group: string;
    };
    availability: {
      availableQuantity: string;
      requestedDateQuantity: string;
      intransitQuantity?: string;
      earliestArrivalDate?: string;
      earliestSdsArrivalDate?: string;
      arrivalDate: string;
      groupArrivalDate: string;
      shipDate: string;
      groupShipDate: string;
      message?: string;
      sourceOrganizationId: string;
      sourceOrganizationCode: string;
    } | null;
    pricing: {
      unitPrice: string | null;
      adjustedUnitPrice: string | null;
    } | null;
    services: (LegacyOrderRequest['items'][number]['relatedServices'][number] & {
      taxAmount?: string;
      pim?: {
        geego_fg_uncrate_required: 'Y' | 'N';
      };
    })[];
  })[];
  address?: {
    phoneNumber: string;
  } | null;
  logistics?: {
    canAccommodate53ftTruck: boolean;
    hasLoadingDock: boolean;
    needLiftGate: boolean;
  } | null;
  delivery?: {
    preference?: 'ASAP' | 'FEWER_TRIPS' | 'SCHEDULE' | null;
    purchaseOrderNumber?: string;
    customerInstructions?: string;
    deliveryInstructions?: string;
    partsShipMethod?: string;
    isEmergencyPartsOrder?: boolean;
    includePricesOnPartsPackingSlip?: boolean;
    appliancesShipSet?: Record<string, ShipSet>;
    /** Format - YYYY-MM-DD */
    partsRequestedShipDate?: string | null;
  } | null;
  // appliancesShipSet: Record<string, ShipSet>;
  originalCartData: {
    accountNumber: string;
    items: Pick<CartItem, 'sk' | 'item'>[];
    source?: 'cart' | 'quick-order';
  };
  finalOrderData?: FinalOrderData;
};

type CartState = {
  snapshot: CartSnapshot | null;
};

const initialState: CartState = {
  snapshot: null,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    saveCartSnapshot: (state, action: PayloadAction<CartSnapshot>) => {
      state.snapshot = action.payload;
    },
    updateSnapshotShipSets: (
      state,
      action: PayloadAction<{
        from?: number;
        to?: number | '';
      }>
    ) => {
      const { from, to } = action.payload;
      const deletedIndex = from || 0;

      state.snapshot?.items?.forEach(item => {
        if (from) {
          const currentIndex = item.shipSet ? +item.shipSet : 0;
          if (from.toString() === item.shipSet) {
            item.shipSet = to?.toString() || '';
          } else if (currentIndex > deletedIndex) {
            item.shipSet = (currentIndex - 1).toString();
          }
        } else {
          item.shipSet = to ? to.toString() : '';
        }
      });
    },
    setCartSnapshotItems: (
      state,
      action: PayloadAction<CartSnapshot['items']>
    ) => {
      if (state.snapshot) {
        state.snapshot.items = action.payload;
      }
    },
    // updateShipSet: (
    //   state,
    //   action: PayloadAction<Record<string, Partial<ShipSet>>>
    // ) => {
    //   if (state.snapshot) {
    //     const snapshot = state.snapshot;
    //     Object.entries(action.payload).forEach(([key, value]) => {
    //       if (snapshot.shipSet[key]) {
    //         snapshot.shipSet[key] = {
    //           ...snapshot.shipSet[key],
    //           ...value,
    //         };
    //       } else {
    //         snapshot.shipSet[key] = value as ShipSet;
    //       }
    //     });
    //   }
    // },
    saveAddress: (state, action: PayloadAction<CartSnapshot['address']>) => {
      if (state.snapshot) {
        state.snapshot.address = action.payload;
      }
    },
    saveLogistics: (
      state,
      action: PayloadAction<CartSnapshot['logistics']>
    ) => {
      if (state.snapshot) {
        state.snapshot.logistics = action.payload;
      }
    },
    saveDelivery: (state, action: PayloadAction<CartSnapshot['delivery']>) => {
      if (state.snapshot) {
        state.snapshot.delivery = action.payload;
      }
    },
    saveFinalOrderData: (state, action: PayloadAction<FinalOrderData>) => {
      if (state.snapshot) {
        state.snapshot.finalOrderData = action.payload;
      }
    },
    updateCartSnapshot: (
      state,
      action: PayloadAction<Partial<CartSnapshot>>
    ) => {
      if (state.snapshot) {
        state.snapshot = {
          ...state.snapshot,
          ...structuredClone(action.payload),
        };
      }
    },
    clearCartSnapshot: state => {
      state.snapshot = null;
    },
  },
});

export const {
  saveCartSnapshot,
  updateSnapshotShipSets,
  setCartSnapshotItems,
  // updateShipSet,
  saveAddress,
  saveLogistics,
  saveDelivery,
  saveFinalOrderData,
  updateCartSnapshot,
  clearCartSnapshot,
} = cartSlice.actions;
export default cartSlice;
